import { ColumnProps } from "@monolith-forensics/monolith-ui";
import { monolithMoment } from "@/utils/date-format";
import { EvidenceAssignedUserBtnSelector } from "../components";
import { EvidenceProgressBtnSelector } from "@/Features/EvidenceProgress";
import { EvidenceStatusBtnSelector } from "@/Features/EvidenceStatus";
import { EvidencePriorityBtnSelector } from "@/Features/EvidencePriority";
import EvidenceNumberCell from "@/components/Evidence/EvidenceNumberCell";
import CaseNumberCell from "@/components/Evidence/CaseNumberCell";
import LocationCell from "@/components/Evidence/LocationCell";
import TruncatedTextCell from "@/components/Evidence/TruncatedTextCell";

export const EvidenceColumnDefinitions: ColumnProps[] = [
  {
    dataField: "uuid",
    caption: "UUID",
    visible: false,
  },
  {
    dataField: "evidence_number",
    caption: "Evidence Number",
    render: ({ rowData }) => {
      return <EvidenceNumberCell rowData={rowData} />;
    },
    visible: true,
  },
  {
    dataField: "case_number",
    caption: "Case Number",
    visible: false,
    render: ({ rowData }) => {
      return <CaseNumberCell rowData={rowData} />;
    },
  },
  {
    dataField: "case_ref",
    caption: "Case Name",
    visible: false,
  },
  {
    dataField: "item_type",
    caption: "Type",
  },
  {
    dataField: "model_name",
    caption: "Item Name",
  },
  {
    dataField: "manufacturer",
    caption: "Provider",
  },
  {
    dataField: "model_number",
    caption: "Model Number",
    visible: false,
  },
  {
    dataField: "serial_number",
    caption: "Unique ID",
    visible: false,
  },
  {
    dataField: "assigned_user",
    caption: "Assigned User",
    visible: true,
    render: ({ rowData, onRowUpdated }) => {
      return (
        <EvidenceAssignedUserBtnSelector
          value={rowData?.assigned_user?.user_id || null}
          query={{
            user_id: [
              ...(Array.isArray(rowData.assigned_user_ids)
                ? rowData.assigned_user_ids
                : []),
              rowData?.case_lead?.case_lead_id,
            ], // include assigned user and case lead
          }}
          onChange={async (user_id) => {
            if (rowData.assigned_user?.user_id !== Number(user_id)) {
              onRowUpdated?.({
                rowData,
                updatedData: {
                  assigned_user_id: Number(user_id),
                  assigned_user: { user_id: Number(user_id) },
                },
              });
            }
          }}
        />
      );
    },
  },
  {
    dataField: "priority_name",
    caption: "Priority",
    render: ({ rowData, onRowUpdated }) => {
      return (
        <EvidencePriorityBtnSelector
          value={rowData?.priority_id || null}
          onChange={(priority, data) => {
            if (rowData.priority_id !== priority) {
              onRowUpdated?.({
                rowData,
                updatedData: {
                  priority_id: data?.priority_id,
                  priority_name: data?.priority_name,
                },
              });
            }
          }}
        />
      );
    },
  },
  {
    dataField: "status_name",
    caption: "Status",
    render: ({ rowData, onRowUpdated }) => {
      return (
        <EvidenceStatusBtnSelector
          value={rowData?.status_name || null}
          onChange={(status, data) => {
            if (rowData.status_name !== status) {
              onRowUpdated?.({
                rowData,
                updatedData: {
                  status_id: data.status_id,
                  status_name: data.status_name,
                },
              });
            }
          }}
        />
      );
    },
  },
  {
    dataField: "progress",
    caption: "Progress",
    render: ({ rowData, onRowUpdated }) => {
      return (
        <EvidenceProgressBtnSelector
          value={rowData?.progress || null}
          onChange={(progress, data) => {
            if (rowData.progress !== progress) {
              onRowUpdated?.({
                rowData,
                updatedData: {
                  progress_id: data.progress_id,
                  progress: data.progress_name,
                },
              });
            }
          }}
        />
      );
    },
  },
  {
    dataField: "created_on",
    caption: "Created On",
    visible: false,
    render: ({ rowData }) => {
      return monolithMoment({
        timestamp: rowData.created_on,
        includeTime: true,
        includeZone: true,
      });
    },
  },
  {
    dataField: "intake_date",
    caption: "Intake Date",
    visible: false,
    render: ({ rowData }) => {
      return monolithMoment({
        timestamp: rowData.intake_date,
      });
    },
  },
  {
    dataField: "last_audit_date",
    caption: "Last Audit Date",
    visible: false,
    render: ({ rowData }) => {
      return monolithMoment({
        timestamp: rowData.last_audit_date,
      });
    },
  },
  {
    dataField: "location_received",
    caption: "Location Received",
    visible: false,
  },
  {
    dataField: "location_name",
    caption: "Current Location",
    visible: false,
    render: ({ rowData }) => <LocationCell rowData={rowData} />,
  },
  {
    dataField: "location_path",
    caption: "Location Path",
    visible: false,
    render: ({ rowData }) => (
      <TruncatedTextCell label="Location Path" value={rowData.location_path} />
    ),
  },
  {
    dataField: "capacity_bytes",
    caption: "Size",
    visible: false,
    render: ({ rowData }) =>
      rowData.capacity
        ? `${rowData.capacity} ${rowData.capacity_unit || ""}`
        : "",
  },
  {
    dataField: "capacity_gb",
    caption: "Size (GB)",
    visible: false,
    render: ({ rowData }) => {
      if (rowData.capacity_gb === null || rowData.capacity_gb === undefined)
        return "";
      return rowData.capacity_gb + " GB";
    },
  },
  {
    dataField: "parent_name",
    caption: "Parent Evidence",
    visible: false,
    render: ({ rowData }) => {
      return (
        <>
          {rowData.is_parent !== 1 ? (
            <EvidenceNumberCell
              rowData={{
                uuid: rowData.parent_uuid,
                evidence_number: rowData.parent_name,
                is_parent: 1,
              }}
            />
          ) : (
            "N/A"
          )}
        </>
      );
    },
  },
  {
    dataField: "received_by",
    caption: "Received By",
    visible: false,
  },
  {
    dataField: "received_from",
    caption: "Received From",
    visible: false,
  },
  {
    dataField: "client_name",
    caption: "Client Name",
    visible: false,
  },
  {
    dataField: "client_organization",
    caption: "Organization",
    visible: false,
  },
  {
    dataField: "office_name",
    caption: "Office",
    visible: false,
  },
  {
    dataField: "linked_contact",
    caption: "Linked Contact",
    visible: false,
    render: ({ rowData }) => {
      if (!rowData?.linked_contact?.name) {
        return null;
      }
      return <span>{rowData?.linked_contact?.name}</span>;
    },
  },
  {
    dataField: "is_parent_child",
    caption: "Parent/Child",
    visible: false,
  },
  {
    dataField: "has_children",
    caption: "Has Children",
    visible: false,
  },
  {
    dataField: "child_count",
    caption: "Child Count",
    visible: false,
  },
  {
    dataField: "is_acquired",
    caption: "Is Acquired",
    visible: false,
  },
  {
    dataField: "acquisition_count",
    caption: "Acquisition Count",
    visible: false,
  },
  {
    dataField: "has_photos",
    caption: "Is Photographed",
    visible: false,
  },
  {
    dataField: "photo_count",
    caption: "Photo Count",
    visible: false,
  },
  {
    dataField: "description",
    caption: "Description",
    render: ({ rowData }) => (
      <TruncatedTextCell label="Description" value={rowData.description} />
    ),
    visible: false,
  },
];
